import { useEffect, useRef } from 'react';

const AUDIO_TYPE = 'audio/mpeg';

const MAX_BUFFER_AHEAD_SECONDS = 360;
const RESUME_BUFFER_AHEAD_SECONDS = 180;

/**
 * A Promise-based wrapper for the SourceBuffer's `appendBuffer` method.
 * The MediaSource API is event-based. This utility hides that complexity by
 * resolving a promise when the 'updateend' event fires, allowing for
 * cleaner async/await syntax in the streaming loop.
 * @param sourceBuffer The MediaSource SourceBuffer to append to.
 * @param buffer The audio data chunk (ArrayBuffer) to append.
 */
const appendBufferAsync = (sourceBuffer: SourceBuffer, buffer: ArrayBuffer) => {
	return new Promise<void>((resolve, reject) => {
		try {
			sourceBuffer.addEventListener('updateend', () => resolve(), { once: true });
			sourceBuffer.appendBuffer(buffer);
		} catch (e) {
			reject(e);
		}
	});
};

/**
 * Computes buffered time ahead of the current playhead.
 */
const getBufferAheadSeconds = (audio: HTMLAudioElement) => {
	try {
		const currentPlaybackTime = audio.currentTime;
		const ranges = audio.buffered;
		for (let i = 0; i < ranges.length; i++) {
			const start = ranges.start(i);
			const end = ranges.end(i);
			if (start <= currentPlaybackTime && currentPlaybackTime <= end) {
				return Math.max(0, end - currentPlaybackTime);
			}
		}
		return 0;
	} catch {
		return 0;
	}
};

/**
 * Waits until bufferAhead drops to or below the resume threshold.
 */
const waitForBufferToDrain = async (
	audio: HTMLAudioElement,
	isMounted: { current: boolean },
	resumeThresholdSeconds: number,
) => {
	return new Promise<void>((resolve) => {
		const evaluateResumeCondition = () => {
			if (!isMounted.current) {
				cleanup();
				resolve();
				return;
			}
			const bufferAheadSeconds = getBufferAheadSeconds(audio);
			if (bufferAheadSeconds <= resumeThresholdSeconds || audio.ended) {
				cleanup();
				resolve();
			}
		};

		const onTimeUpdate = () => evaluateResumeCondition();
		const onSeeked = () => evaluateResumeCondition();
		audio.addEventListener('timeupdate', onTimeUpdate);
		audio.addEventListener('seeked', onSeeked);
		// run an immediate check in case we're already under the threshold
		evaluateResumeCondition();

		function cleanup() {
			audio.removeEventListener('timeupdate', onTimeUpdate);
			audio.removeEventListener('seeked', onSeeked);
		}
	});
};

/**
 * Reads data chunks from a ReadableStream and feeds them into a MediaSource.
 * This function is the core of the streaming logic.
 * It's designed to be cancellable by checking the `isMounted` ref, which prevents
 * state updates or errors from being dispatched after the component has unmounted.
 * @param mediaSource The active MediaSource instance.
 * @param stream The ReadableStream of audio data.
 * @param isMounted A ref object to track if the component is still mounted.
 * @param audio The HTMLAudioElement consuming this MediaSource.
 * @param onError Callback to dispatch errors.
 * @param setStreamEnded Callback to signal that the stream has finished.
 */
const streamToMediaSource = async (
	mediaSource: MediaSource,
	stream: ReadableStream<Uint8Array>,
	isMounted: { current: boolean },
	audio: HTMLAudioElement,
	onError: (error: Error) => void,
	setStreamEnded?: (streamEnded: boolean) => void,
) => {
	const sourceBuffer = mediaSource.addSourceBuffer(AUDIO_TYPE);
	sourceBuffer.mode = 'sequence';
	const reader = stream.getReader();

	try {
		while (isMounted.current) {
			// Backpressure: if bufferAhead is at or above the cap, wait for it to drain
			const bufferAheadSeconds = getBufferAheadSeconds(audio);
			if (bufferAheadSeconds >= MAX_BUFFER_AHEAD_SECONDS) {
				await waitForBufferToDrain(audio, isMounted, RESUME_BUFFER_AHEAD_SECONDS);
				if (!isMounted.current) break;
			}
			const { done, value } = await reader.read();
			if (done) {
				break;
			}

			if (!isMounted.current) {
				break;
			}
			// @ts-expect-error - TS2345 Typescript 5.9.2 upgrade
			await appendBufferAsync(sourceBuffer, value);
		}

		if (isMounted.current) {
			if (mediaSource.readyState === 'open') {
				mediaSource.endOfStream();
			}
			setStreamEnded?.(true);
		}
	} catch (e) {
		if (isMounted.current) {
			onError(e as Error);
		}
	}
};

/**
 * A React hook to stream audio into an HTMLAudioElement using the MediaSource API.
 * It returns a ref (`audioRef`) that should be attached to an `<audio>` tag.
 * @param stream The ReadableStream of the audio to be played.
 * @param onError A callback function to handle any errors during streaming.
 * @param setStreamEnded A callback to notify the parent component that the stream has ended.
 */
export const useAudioStream = (
	stream: ReadableStream<Uint8Array> | undefined,
	onError: (error: Error) => void,
	setStreamEnded?: (streamEnded: boolean) => void,
) => {
	const audioRef = useRef<HTMLAudioElement>(null);

	useEffect(() => {
		const audio = audioRef.current;

		if (!audio || !stream) {
			return;
		}

		const isMounted = { current: true };

		const mediaSource = new MediaSource();
		const url = URL.createObjectURL(mediaSource);
		audio.src = url;
		audio.load();

		const onSourceOpen = async () => {
			try {
				URL.revokeObjectURL(url);
				await streamToMediaSource(mediaSource, stream, isMounted, audio, onError, setStreamEnded);
			} catch (e) {
				if (isMounted.current) {
					onError(e as Error);
				}
			}
		};

		mediaSource.addEventListener('sourceopen', onSourceOpen);

		return () => {
			isMounted.current = false;
			mediaSource.removeEventListener('sourceopen', onSourceOpen);
			if (mediaSource.readyState === 'open') {
				try {
					mediaSource.endOfStream();
				} catch (e) {
					// Errors during cleanup can be safely ignored.
				}
			}
		};
	}, [stream, onError, setStreamEnded]);

	return { audioRef };
};
